import React from "react";
import {Form, Stack, SubmitButton, View} from "@unibuddy/patron";
import * as yup from "yup";
import {ErrorMessage, FormikHelpers} from "formik";
import {useTranslation} from "@unibuddy/intl";
import {TFunction} from "i18next";

import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";
import {
    AreaOfStudyFormField,
    CountryFormField,
    CustomAttributesFormFields,
    DateOfEntryFormField,
    DegreeLevelsFormField,
    DomesticRegionFormField,
    InstitutionMarketingConsent,
    LocalePreferenceFormField,
} from "./RegisterPage2FormComponents";
import {CountryField, DomesticRegionField, Locale, Maybe} from "../../../../types/generated";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_COMPLETE_PROFILE} from "../../../../translations/signup";
import {INTL_GENERIC_ERROR} from "../../../../translations/errorMessage";
import {
    FIRST_NAME_REQUIRED,
    LAST_NAME_REQUIRED,
} from "../../../../shared/Shared/validations/validationMessages";
import {NameFormField} from "./RegisterPageComponents/NameFormField";

type RegisterTwoKnownFormValues = {
    firstName: string;
    lastName: string;
    // dateOfEntry format is a YYYY-MM string
    dateOfEntry?: string;
    countrySelection?: {
        label: string;
        value: string;
    };
    domesticRegionSelection?: DomesticRegionField;
    degreeLevel?: string;
    degrees?: [
        {
            label: string;
            value: string;
        },
    ];
    localePreference?: {
        label: string;
        value: Locale;
    };
    acceptMarketing: boolean;
};

type RegisterTwoUnknownFormValues = {
    [key: string]: unknown;
};

export type RegisterTwoFormValues = RegisterTwoKnownFormValues & RegisterTwoUnknownFormValues;

type NewRegisterPage2FormProps = {
    handleSubmit: (values: RegisterTwoKnownFormValues) => Promise<void>;
    allCountries: Array<Maybe<CountryField>>;
    applicableLocales: Array<Maybe<Locale>>;
    isSsoUser: boolean;
    initialValues: {
        acceptMarketing: boolean;
        serverError: string;
        firstName: string;
        lastName: string;
    };
};

function makeValidationSchema(
    t: TFunction<[IntlNamespaces.SIGNUP, IntlNamespaces.SETTINGS, IntlNamespaces.LOGIN]>,
) {
    return {
        firstName: yup
            .string()
            .trim()
            .required(t("validations:firstNameRequired", FIRST_NAME_REQUIRED)),
        lastName: yup
            .string()
            .trim()
            .required(t("validations:lastNameRequired", LAST_NAME_REQUIRED)),
    };
}

export function RegisterPage2Form({
    handleSubmit,
    allCountries,
    applicableLocales,
    isSsoUser,
    initialValues,
}: NewRegisterPage2FormProps) {
    const {t} = useTranslation([IntlNamespaces.SIGNUP, IntlNamespaces.ERROR_MESSAGE]);
    return (
        <Form
            initialValues={initialValues}
            validationSchema={makeValidationSchema(t)}
            onSubmit={async (
                values: RegisterTwoFormValues,
                {setSubmitting, setFieldError}: FormikHelpers<RegisterTwoFormValues>,
            ) => {
                setSubmitting(true);
                try {
                    await handleSubmit(values);
                } catch (error) {
                    setFieldError(
                        "serverError",
                        t(
                            `${IntlNamespaces.ERROR_MESSAGE}:${INTL_GENERIC_ERROR.key}`,
                            INTL_GENERIC_ERROR.defaultValue,
                        ),
                    );
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(props: {
                values: RegisterTwoFormValues;
                setFieldValue: (field: string, newValue: string) => void;
                errors: {serverError: string};
            }) => {
                const {values, setFieldValue, errors} = props;

                return (
                    <Stack space="medium-large">
                        <View gap="medium">
                            {isSsoUser && <NameFormField />}
                            <DateOfEntryFormField />
                            <CountryFormField allCountries={allCountries} />
                            <DomesticRegionFormField
                                values={values}
                                setFieldValue={setFieldValue}
                            />
                            <DegreeLevelsFormField />
                            <AreaOfStudyFormField values={values} />
                            <LocalePreferenceFormField applicableLocales={applicableLocales} />
                            <CustomAttributesFormFields />
                            <InstitutionMarketingConsent />
                        </View>
                        {errors.serverError && (
                            <ErrorMessage name="serverError" component={AuthFormError} />
                        )}
                        <SubmitButton color="university" block size="lg">
                            {t(
                                `${IntlNamespaces.SIGNUP}:${INTL_COMPLETE_PROFILE.key}`,
                                INTL_COMPLETE_PROFILE.defaultValue,
                            )}
                        </SubmitButton>
                    </Stack>
                );
            }}
        </Form>
    );
}
